import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-dea6c8b2"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "send-verify__header" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", { class: "send-verify__title" }, "Verify transaction", -1))
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "send-verify__description" }, " Double check the information and confirm transaction. ", -1))
const _hoisted_4 = { class: "send-verify__block" }
const _hoisted_5 = { class: "send-verify__block-item" }
const _hoisted_6 = { class: "send-verify__block-item" }
const _hoisted_7 = { class: "send-verify__block-item" }
const _hoisted_8 = { class: "send-verify__block-item" }

import WhiteWrapper from "@/components/white-wrapper/index.vue";
import ButtonsBlock from "@/components/buttons-block/index.vue";
import BaseButton from "@/components/base-button/index.vue";
import BackButton from "@/components/back-button/index.vue";
import SendError from "../send-transaction/components/send-error.vue";
import SendVerifyItem from "./components/send-verify-item.vue";
import SendVerifyAmount from "./components/send-verify-amount.vue";
import SendVerifyFee from "./components/send-verify-fee.vue";
import SendVerifySuccess from "./components/send-verify-success.vue";

import { Account } from "@/types/account";
import { Token } from "@/types/token";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  accounts,
  apiPromise,
  nativeBalances,
  nativeToken,
  selectedNetwork,
  signer,
} from "@/stores";
import { encodeSubstrateAddress } from "@/utils";
import { formatAddress } from "@/utils/filters";
import createIcon from "@/libs/identicon/polkadot";
import { GasFeeInfo } from "@/types/transaction";
import { isValidDecimals, toBase } from "@/utils/units";
import { sendExtrinsic } from "@/utils/extrinsic";
import { toBN } from "web3-utils";
import { getGasFeeInfo } from "@/utils/fee";
import AccountsState from "@/state/accounts";


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const router = useRouter();
const route = useRoute();
const accountsState = new AccountsState();

const fromAccount = ref<Account>();
const toAccount = ref<Account>();
const amount = ref<string>();
const fee = ref<GasFeeInfo>();
const selectedAsset = ref<Token>(nativeToken.value);
const hasEnough = ref(true);
const isSend = ref<boolean>(false);

onMounted(() => {
  if (!route.query.from || !route.query.to || !route.query.amount) {
    router.push({ name: "send" });
    return;
  }

  const foundFrom = accounts.value.find(
    (item) => item.address === route.query.from
  );
  let foundTo = accounts.value.find((item) => item.address === route.query.to);
  if (!foundTo) {
    const toAddress = route.query.to.toString();
    const isValidAddress = encodeSubstrateAddress(toAddress);
    if (isValidAddress) {
      foundTo = {
        id: Number.MAX_SAFE_INTEGER,
        name: "",
        image: createIcon(toAddress),
        address: formatAddress(toAddress),
        isLedger: false,
      };
    }
  }
  const convertedAmount = Number(route.query.amount);

  if (foundFrom && foundTo && convertedAmount) {
    fromAccount.value = foundFrom;
    toAccount.value = foundTo;
    amount.value = convertedAmount.toString();
  } else {
    router.push({ name: "send" });
  }
});

const edWarn = computed(() => {
  if (
    !fee.value ||
    !amount.value ||
    !fromAccount.value ||
    !isValidDecimals(amount.value, selectedAsset.value.decimals)
  ) {
    return false;
  }

  const rawAmount = toBN(
    toBase(amount.value?.toString() || "0", selectedAsset.value.decimals)
  );
  const ed = toBN(
    toBase(
      selectedAsset.value.existentialDeposit.toString() || "0",
      selectedAsset.value.decimals
    )
  );
  const userBalance = toBN(
    toBase(
      nativeBalances[fromAccount.value.address]?.available.toString() || "0",
      selectedAsset.value.decimals
    )
  );

  const txFee = toBN(
    toBase(fee.value.nativeValue.toString(), selectedAsset.value.decimals)
  );
  return userBalance.sub(txFee).sub(rawAmount).lt(ed);
});

watch(
  [selectedAsset, amount, nativeBalances, toAccount, selectedNetwork],
  async () => {
    if (
      amount.value &&
      selectedAsset.value &&
      toAccount.value &&
      fromAccount.value
    ) {
      if (
        !isValidDecimals(amount.value.toString(), selectedAsset.value.decimals)
      ) {
        hasEnough.value = false;
        return;
      }

      const api = await apiPromise.value;

      const rawAmount = toBN(
        toBase(amount.value?.toString() || "0", selectedAsset.value.decimals)
      );

      const rawBalance = toBN(
        toBase(
          nativeBalances[fromAccount.value.address]?.available.toString() ||
            "0",
          selectedAsset.value.decimals
        )
      );

      if (rawAmount.gt(rawBalance)) {
        hasEnough.value = false;
      } else {
        hasEnough.value = true;
      }

      const transferType = "all";

      const tx = await sendExtrinsic(
        api,
        toAccount.value.address,
        rawAmount.toString(),
        transferType
      );

      fee.value = await getGasFeeInfo(tx, fromAccount.value.address);
    }
  }
);

const nextAction = async () => {
  if (!toAccount.value?.address || !amount.value || !fromAccount.value) {
    return;
  }

  const api = await apiPromise.value;
  const sendAmount = toBase(amount.value, selectedAsset.value.decimals);
  const transferType = "transfer";

  await accountsState.addRecentAccount(toAccount.value);

  const tx = await sendExtrinsic(
    api,
    toAccount.value.address,
    sendAmount,
    transferType
  );

  const unsubscribe = await tx.signAndSend(
    fromAccount.value.address,
    {
      signer: signer.value,
      nonce: -1,
    },
    async (result) => {
      if (!result || !result.status) {
        return;
      }

      if (result.status.isFinalized || result.status.isInBlock) {
        result.events
          .filter(({ event: { section } }) => section === "system")
          .forEach(({ event: { method } }): void => {
            if (method === "ExtrinsicFailed") {
              // Handle error
            } else if (method === "ExtrinsicSuccess") {
              // Handle succes
            }
          });
      } else if (result.isError) {
        // Handle error
      }

      if (result.isCompleted) {
        unsubscribe();
      }
    }
  );

  isSend.value = true;
};

const back = () => {
  router.go(-1);
};

return (_ctx: any,_cache: any) => {
  return (!isSend.value)
    ? (_openBlock(), _createBlock(WhiteWrapper, {
        key: 0,
        class: "send-verify__wrap"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createVNode(BackButton, { action: back }),
            _hoisted_2
          ]),
          _hoisted_3,
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(SendVerifyItem, {
                account: fromAccount.value,
                amount: 
            _unref(nativeBalances)[fromAccount.value?.address || '']?.available?.toNumber()
          ,
                title: "From"
              }, null, 8, ["account", "amount"])
            ]),
            _createElementVNode("div", _hoisted_6, [
              _createVNode(SendVerifyItem, {
                account: toAccount.value,
                amount: 
            _unref(nativeBalances)[toAccount.value?.address || '']?.available?.toNumber()
          ,
                title: "To"
              }, null, 8, ["account", "amount"])
            ]),
            _createElementVNode("div", _hoisted_7, [
              _createVNode(SendVerifyAmount, {
                token: selectedAsset.value,
                amount: amount.value
              }, null, 8, ["token", "amount"])
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(SendVerifyFee, { fee: fee.value }, null, 8, ["fee"])
            ])
          ]),
          (edWarn.value)
            ? (_openBlock(), _createBlock(SendError, {
                key: 0,
                token: selectedAsset.value
              }, null, 8, ["token"]))
            : _createCommentVNode("", true),
          _createVNode(ButtonsBlock, null, {
            default: _withCtx(() => [
              _createVNode(BaseButton, {
                title: "Confirm and send",
                action: nextAction,
                send: true
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }))
    : (_openBlock(), _createBlock(WhiteWrapper, {
        key: 1,
        class: "send-verify__wrap"
      }, {
        default: _withCtx(() => [
          _createVNode(SendVerifySuccess)
        ]),
        _: 1
      }))
}
}

})